import React, { useEffect } from 'react';
import setSeo from '../../utils/seo.js';
import BackToTop from '../../components/BackToTop.jsx';
import case3 from '../../assets/case3.webp';

/**
 * Individual case study: Marketing Industry – LinkedIn Automation with AI Feedback Loops
 * Full case study page with detailed implementation and results
 */
const LinkedInAutomation = () => {
  useEffect(() => {
    setSeo({
      title: 'LinkedIn Marketing Automation Case Study - 40% Better Leads | Aakeeo',
      description:
        'See how our AI-powered LinkedIn automation system reduced campaign preparation from 2 days to 2 hours and improved lead qualification accuracy by 40% for a marketing agency.',
      keywords: ['LinkedIn automation', 'marketing automation', 'AI marketing', 'lead generation automation', 'social media automation', 'LinkedIn case study', 'AI feedback loops'],
      canonical: window.location.href,
      og: {
        type: 'article',
        image: 'https://aakeeo.com' + case3
      },
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': 'Marketing Industry – LinkedIn Automation with AI Feedback Loops',
        'description': 'An automated LinkedIn marketing system that reduced campaign preparation from 2 days to 2 hours and improved lead qualification accuracy by 40%.',
        'image': 'https://aakeeo.com' + case3,
        'author': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies'
        },
        'publisher': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies',
          'logo': {
            '@type': 'ImageObject',
            'url': 'https://aakeeo.com/Aakeeo_favicon.png'
          }
        }
      }
    });
  }, []);
  return (
    <div className="case-study-page">
      <section className="page-hero case-studies-hero">
        <video className="page-hero-video" autoPlay muted loop>
          <source src="/background.mp4" type="video/mp4" />
        </video>
        <div className="page-hero-content">
          <h1 className="page-hero-title">Marketing Industry – LinkedIn Automation with AI Feedback Loops</h1>
          <p className="page-hero-subtitle">An automated LinkedIn marketing system that reduced campaign preparation from 2 days to 2 hours and improved lead qualification accuracy by 40%.</p>
        </div>
      </section>

      <article className="section reveal">
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 2rem' }}>
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <img
              src={case3}
              alt="LinkedIn Automation Success Story"
              style={{
                width: '100%',
                maxWidth: '600px',
                height: '300px',
                objectFit: 'cover',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}
            />
          </div>

          <div style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-light)' }}>
            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Challenge
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              Marketing teams needed a system to create, iterate, and publish LinkedIn campaigns efficiently. Manual drafting, approvals, and lead follow-up consumed days, often delaying campaigns.
            </p>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Requirements
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>AI-assisted content drafting with human-in-the-loop approvals</li>
              <li style={{ marginBottom: '0.5rem' }}>Seamless media upload and publishing to LinkedIn</li>
              <li style={{ marginBottom: '0.5rem' }}>Engagement monitoring (comments, reactions)</li>
              <li style={{ marginBottom: '0.5rem' }}>Lead qualification and automated direct outreach</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Solution Architecture
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              We created a LinkedIn Marketing Agent orchestrated through n8n + Telegram bot integration:
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              1. Telegram Bot Input
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Users initiate campaigns through a custom Telegram bot.
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              2. AI Drafting Loop
            </h3>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Drafts LinkedIn post content using GPT-based AI agents</li>
              <li style={{ marginBottom: '0.5rem' }}>Sends draft to Telegram for review</li>
              <li style={{ marginBottom: '0.5rem' }}>Incorporates user feedback iteratively (chat context maintained)</li>
              <li style={{ marginBottom: '0.5rem' }}>Final approval triggers publishing</li>
            </ul>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              3. Media Handling
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Prompts user to attach images/documents → Uploads with post.
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              4. Engagement Analysis
            </h3>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Scrapes comments and reactions from LinkedIn API</li>
              <li style={{ marginBottom: '0.5rem' }}>AI agent evaluates sentiment and identifies potential leads</li>
              <li style={{ marginBottom: '0.5rem' }}>Leads automatically receive DMs with tailored marketing messages</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Technologies Used
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>n8n for orchestration</li>
              <li style={{ marginBottom: '0.5rem' }}>Telegram Bot API for user interface</li>
              <li style={{ marginBottom: '0.5rem' }}>LinkedIn API (Unofficial / scraping workflows) for posting and engagement data</li>
              <li style={{ marginBottom: '0.5rem' }}>LLM APIs for iterative content refinement</li>
              <li style={{ marginBottom: '0.5rem' }}>MongoDB for storing campaign logs and chat history</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Impact
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'rgba(230, 57, 70, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--primary-color)', fontSize: '1.5rem', marginBottom: '0.5rem' }}>2 days → 2 hours</h3>
                <p style={{ margin: 0 }}>Campaign Preparation Cycle</p>
              </div>
              <div style={{
                background: 'rgba(0, 119, 182, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--accent-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>60%</h3>
                <p style={{ margin: 0 }}>Faster Approvals</p>
              </div>
              <div style={{
                background: 'rgba(72, 202, 228, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--tertiary-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>40%</h3>
                <p style={{ margin: 0 }}>Improved Lead Qualification</p>
              </div>
            </div>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Fully automated end-to-end marketing workflow with human oversight</li>
            </ul>

            <div style={{
              background: 'rgba(230, 57, 70, 0.1)',
              padding: '1.5rem',
              borderRadius: '10px',
              borderLeft: '4px solid var(--primary-color)',
              marginTop: '2rem'
            }}>
              <p style={{ margin: 0, fontStyle: 'italic' }}>
                Ready to accelerate your LinkedIn marketing campaigns? Contact Aakeeo today to discover how our AI-powered automation solutions can transform your content creation and lead generation processes.
              </p>
            </div>
          </div>
        </div>
      </article>
        <BackToTop />
    </div>
  );
};

export default LinkedInAutomation;